import React from 'react';
import { Select } from 'antd';
import type { MusicTrack } from '../../audio';
import { colors, dimensions, typography } from '../../theme';
import { VolumeRow } from './volume-row';

type MusicTabProps = {
  tracks: MusicTrack[] | null;
  isLoading: boolean;
  selectedTrackId: string | null;
  onSelectTrackId: (trackId: string | null) => void;
  volume: number;
  onVolumeChange: (next: number) => void;
};

/**
 * Tab Música del SoundPanel. Componente "view" controlado.
 *
 * El dropdown se renderea con `dropdownRender` envolviendo el menú en un div
 * con `data-panel-id='sound'`. Eso evita que el handler de outside-click de
 * `usePanels` cierre el panel cuando el user clickea una opción (el menú vive
 * en un portal fuera del DOM ancestro del panel; sin la marca contaría como
 * "click afuera").
 */
export const MusicTab: React.FC<MusicTabProps> = ({
  tracks,
  isLoading,
  selectedTrackId,
  onSelectTrackId,
  volume,
  onVolumeChange
}) => {
  const options = (tracks ?? []).map(track => ({ value: track.id, label: track.name }));
  const empty = !isLoading && tracks !== null && tracks.length === 0;
  const selectedTrack = tracks?.find(t => t.id === selectedTrackId) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: dimensions.spacing.lg }}>
      <label
        style={{
          fontSize: typography.fontSize.sm,
          color: colors.textSecondary,
          fontFamily: typography.fontFamily.ui,
          fontWeight: typography.fontWeight.medium,
          textTransform: 'uppercase',
          letterSpacing: typography.letterSpacing.wider
        }}
      >
        Música de fondo
      </label>
      <Select<string>
        value={selectedTrackId ?? undefined}
        onChange={value => onSelectTrackId(value ?? null)}
        options={options}
        loading={isLoading}
        placeholder={empty ? 'No hay tracks disponibles' : 'Seleccionar track'}
        style={{ width: '100%' }}
        disabled={empty}
        allowClear
        popupRender={menu => <div data-panel-id='sound'>{menu}</div>}
      />
      {selectedTrack && (
        <VolumeRow audioUrl={selectedTrack.url} volume={volume} onVolumeChange={onVolumeChange} />
      )}
      {empty && (
        <span
          style={{
            fontSize: typography.fontSize.sm,
            color: colors.textMuted,
            fontFamily: typography.fontFamily.ui
          }}
        >
          Pedile al equipo que sume tracks de música a tu librería.
        </span>
      )}
    </div>
  );
};
